import { useEffect, useState, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../constants/permissions'
import './AdminLayout.css'

/**
 * Componente AdminLayout
 * Layout para el panel administrativo con sidebar
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido del panel
 */
function AdminLayout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { userData, logout } = useAuth()
  const { can, isSuperAdmin, hasAdminAccess } = usePermissions()
  const sanitizeEnvValue = (value) => typeof value === 'string' ? value.replace(/^['"]|['"]$/g, '') : value
  const systemName = sanitizeEnvValue(import.meta.env.VITE_SYSTEM_NAME) || 'PinoHome'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const wideContentRoutes = [
    '/admin/configuraciones/reglas-negocio',
    '/admin/configuraciones/notificaciones',
    '/admin/ventas',
    '/admin/pedidos',
    '/admin/productos'
  ]
  const isWideContent = wideContentRoutes.some((basePath) =>
    location.pathname.startsWith(basePath)
  )

  // Estado para acordeones del menú (persistir en localStorage)
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    const saved = localStorage.getItem('adminAccordions')
    return saved ? JSON.parse(saved) : { productos: true, ventas: true }
  })

  // Items del menú de navegación (estructura con acordeones)
  // Se filtran según permisos del usuario
  const menuStructure = useMemo(() => {
    const items = [
      { 
        type: 'link', 
        path: '/admin', 
        label: 'Dashboard', 
        icon: 'grid_view',
        section: 'Principal',
        permission: null // Siempre visible si tiene acceso al admin
      },
      {
        type: 'accordion',
        key: 'productos',
        label: 'Módulo Productos',
        icon: 'inventory_2',
        section: 'Operaciones',
        permission: MODULES.PRODUCTS,
        items: [
          { 
            path: '/admin/productos', 
            label: 'Productos', 
            icon: 'inventory_2',
            permission: MODULES.PRODUCTS
          },
          { 
            path: '/admin/ofertas', 
            label: 'Ofertas', 
            icon: 'local_offer',
            permission: MODULES.OFFERS
          }
        ]
      },
      {
        type: 'accordion',
        key: 'ventas',
        label: 'Módulo Ventas',
        icon: 'shopping_cart',
        section: 'Operaciones',
        permission: MODULES.ORDERS, // Si puede ver pedidos o ventas
        items: [
          { 
            path: '/admin/pedidos', 
            label: 'Pedidos', 
            icon: 'shopping_cart',
            permission: MODULES.ORDERS
          },
          { 
            path: '/admin/ventas', 
            label: 'Ventas', 
            icon: 'point_of_sale',
            permission: MODULES.SALES
          }
        ]
      },
      { 
        type: 'link', 
        path: '/admin/etiquetas', 
        label: 'Etiquetas', 
        icon: 'qr_code_2',
        section: 'Operaciones',
        permission: MODULES.LABELS
      },
      { 
        type: 'link', 
        path: '/admin/pedidos-compra', 
        label: 'Compras', 
        icon: 'shopping_bag',
        section: 'Operaciones',
        permission: MODULES.PURCHASE_ORDERS
      },
      { 
        type: 'link', 
        path: '/admin/insumos', 
        label: 'Insumos', 
        icon: 'extension',
        section: 'Operaciones',
        permission: MODULES.SUPPLIES
      },
      { 
        type: 'link', 
        path: '/admin/clientes', 
        label: 'Clientes', 
        icon: 'people',
        section: 'Operaciones',
        permission: MODULES.CUSTOMERS
      },
      { 
        type: 'link', 
        path: '/admin/stock', 
        label: 'Stock', 
        icon: 'layers',
        section: 'Logística',
        permission: MODULES.STOCK
      },
      { 
        type: 'link', 
        path: '/admin/depositos', 
        label: 'Depósitos', 
        icon: 'warehouse',
        section: 'Logística',
        permission: MODULES.WAREHOUSES
      },
      { 
        type: 'link', 
        path: '/admin/proveedores', 
        label: 'Proveedores', 
        icon: 'local_shipping',
        section: 'Logística',
        permission: MODULES.SUPPLIERS
      },
      { 
        type: 'link', 
        path: '/admin/usuarios', 
        label: 'Usuarios', 
        icon: 'group_work',
        section: 'Administración',
        permission: MODULES.USERS
      },
      { 
        type: 'link', 
        path: '/admin/seguridad', 
        label: 'Seguridad', 
        icon: 'admin_panel_settings',
        section: 'Administración',
        permission: MODULES.SECURITY,
        superAdminOnly: true 
      },
      { 
        type: 'link', 
        path: '/admin/contabilidad', 
        label: 'Contabilidad', 
        icon: 'account_balance',
        section: 'Administración',
        permission: MODULES.REPORTS
      },
      { 
        type: 'link', 
        path: '/admin/configuraciones', 
        label: 'Configuraciones', 
        icon: 'settings',
        section: 'Administración',
        permission: MODULES.CONFIGURATIONS
      }
    ]

    // Filtrar items según permisos
    return items.filter(item => {
      // Items solo para superadmin
      if (item.superAdminOnly && !isSuperAdmin()) {
        return false
      }
      
      // Si no requiere permiso específico, siempre visible
      if (!item.permission) {
        return true
      }
      
      // Verificar permiso
      return can(item.permission, ACTIONS.VIEW)
    }).map(item => {
      // Filtrar items de acordeones también
      if (item.type === 'accordion' && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            if (!subItem.permission) return true
            return can(subItem.permission, ACTIONS.VIEW)
          })
        }
      }
      return item
    }).filter(item => {
      // Si es un acordeón sin items, no mostrarlo
      if (item.type === 'accordion' && (!item.items || item.items.length === 0)) {
        return false
      }
      return true
    })
  }, [can, isSuperAdmin])

  const hasAccess = hasAdminAccess()

  // Obtener el título de la página actual (usando useMemo para recalcular cuando cambie la ruta)
  const pageTitle = useMemo(() => {
    const currentPath = location.pathname
    
    // Si estamos en la raíz del admin, mostrar Dashboard
    if (currentPath === '/admin' || currentPath === '/admin/') {
      return 'Dashboard'
    }
    
    // Buscar en items de acordeones primero (tienen prioridad)
    for (const item of menuStructure) {
      if (item.type === 'accordion' && item.items) {
        const subItem = item.items.find(sub => {
          // Verificar si la ruta actual coincide o empieza con la ruta del menú
          return currentPath === sub.path || currentPath.startsWith(sub.path + '/')
        })
        if (subItem) return subItem.label
      }
    }
    
    // Buscar en links directos (ordenar por longitud de path descendente para que coincidan primero las rutas más específicas)
    const sortedLinks = menuStructure
      .filter(item => item.type === 'link')
      .sort((a, b) => b.path.length - a.path.length)
    
    const directLink = sortedLinks.find(item => {
      return currentPath === item.path || currentPath.startsWith(item.path + '/')
    })
    if (directLink) return directLink.label

    // Si no se encuentra, intentar inferir desde la ruta
    const pathParts = currentPath.split('/').filter(Boolean)
    if (pathParts.length >= 2) {
      const section = pathParts[1] // admin, productos, pedidos, etc.
      const sectionLabels = {
        'productos': 'Productos',
        'pedidos': 'Pedidos',
        'ventas': 'Ventas',
        'ofertas': 'Ofertas',
        'etiquetas': 'Etiquetas',
        'pedidos-compra': 'Compras',
        'insumos': 'Insumos',
        'clientes': 'Clientes',
        'stock': 'Stock',
        'depositos': 'Depósitos',
        'proveedores': 'Proveedores',
        'usuarios': 'Usuarios',
        'contabilidad': 'Contabilidad',
        'configuraciones': 'Configuraciones'
      }
      if (sectionLabels[section]) {
        return sectionLabels[section]
      }
    }

    return 'Admin'
  }, [location.pathname, menuStructure])

  useEffect(() => {
    if (!hasAccess) {
      navigate('/')
    }
  }, [hasAccess, navigate])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  if (!hasAccess) {
    return null
  }

  // Función para toggle de acordeones
  const toggleAccordion = (key) => {
    setExpandedAccordions(prev => {
      const newState = {
        ...prev,
        [key]: !prev[key]
      }
      localStorage.setItem('adminAccordions', JSON.stringify(newState))
      return newState
    })
  }

  // Verificar si una ruta está activa (incluyendo sub-rutas)
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Verificar si un acordeón tiene alguna ruta activa
  const isAccordionActive = (items) => {
    return items.some(item => isActive(item.path))
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const displayName = userData?.displayName || userData?.name || 'Admin'
  const role = (userData?.role || '').toString()
  const roleLabel = role ? role : 'admin'
  const roleBadgeClass = role.toLowerCase() === 'superadmin'
    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
    : 'bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200'

  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('')

  const breadcrumbCurrent = pageTitle === 'Dashboard' ? 'Resumen' : pageTitle

  const sectionOrder = ['Principal', 'Operaciones', 'Logística', 'Administración']
  const menuSections = sectionOrder
    .map(sectionLabel => ({
      sectionLabel,
      items: menuStructure.filter(item => item.section === sectionLabel)
    }))
    .filter(section => section.items.length > 0)

  const toggleDarkMode = () => {
    document.documentElement.classList.toggle('dark')
  }

  return (
    <div className="ph-admin-theme bg-[#f8fafc] dark:bg-[#0f172a] text-slate-900 dark:text-slate-100 min-h-screen flex">
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          aria-label="Cerrar menú"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`w-64 bg-[#1e293b] text-slate-300 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-icons-round text-emerald-400">dashboard_customize</span>
            <h1 className="font-bold text-lg tracking-tight text-white">
              Panel Admin <span className="font-light text-slate-400"></span>
            </h1>
          </div>
          <button
            type="button"
            className="md:hidden text-slate-400"
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <span className="material-icons-round">menu_open</span>
          </button>
        </div>

        <nav className="flex-1 px-4 pb-4 overflow-y-auto ph-admin-sidebar-scroll space-y-1">
          {menuSections.map(section => (
            <div key={section.sectionLabel}>
              <p className={`px-3 text-[10px] font-semibold text-slate-300 uppercase tracking-wider ${
                section.sectionLabel === 'Principal' ? 'mb-2' : 'mt-6 mb-2'
              }`}>
                {section.sectionLabel}
              </p>
              {section.items.map(item => {
                if (item.type === 'link') {
                  const active = isActive(item.path)
                  const linkClass = active
                    ? 'bg-emerald-400/20 text-emerald-100 rounded-md border-l-2 border-emerald-400'
                    : 'bg-emerald-400/10 text-emerald-200 rounded-md border-l-2 border-emerald-400 hover:bg-emerald-400/15 hover:text-emerald-100'

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center gap-3 px-3 py-2 transition-colors ${linkClass}`}
                    >
                      <span className={`material-icons-round text-[20px] ${
                        active ? 'text-emerald-100' : 'text-emerald-200 group-hover:text-emerald-100'
                      }`}>
                        {item.icon}
                      </span>
                      <span className={`text-sm font-medium ${active ? '' : ''}`}>{item.label}</span>
                    </Link>
                  )
                }

                if (item.type === 'accordion') {
                  const isExpanded = expandedAccordions[item.key]
                  const hasActiveChild = isAccordionActive(item.items)

                  return (
                    <div key={item.key}>
                      <button
                        type="button"
                        onClick={() => toggleAccordion(item.key)}
                        className={`group w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                          hasActiveChild
                            ? 'bg-emerald-400/20 text-emerald-100 border-l-2 border-emerald-400'
                            : 'bg-emerald-400/10 text-emerald-200 border-l-2 border-emerald-400 hover:bg-emerald-400/15 hover:text-emerald-100'
                        }`}
                      >
                        <span className={`material-icons-round text-[20px] ${
                          hasActiveChild ? 'text-emerald-100' : 'text-emerald-200 group-hover:text-emerald-100'
                        }`}>
                          {item.icon}
                        </span>
                        <span className="text-sm font-medium">{item.label}</span>
                        <span className={`material-icons-round text-[16px] ml-auto transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}>
                          chevron_right
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="pl-7 space-y-1 mt-1">
                          {item.items.map(subItem => {
                            const active = isActive(subItem.path)
                            const subClass = active
                              ? 'bg-emerald-400/20 text-emerald-100 rounded-md border-l-2 border-emerald-400'
                              : 'bg-emerald-400/10 text-emerald-200 rounded-md border-l-2 border-emerald-400 hover:bg-emerald-400/15 hover:text-emerald-100'

                            return (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`group flex items-center gap-3 px-3 py-2 transition-colors ${subClass}`}
                              >
                                <span className={`material-icons-round text-[20px] ${
                                  active ? 'text-emerald-100' : 'text-emerald-200 group-hover:text-emerald-100'
                                }`}>
                                  {subItem.icon}
                                </span>
                                <span className="text-sm font-medium">{subItem.label}</span>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                return null
              })}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2 text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-icons-round text-[20px]">home</span>
            <span className="text-sm font-medium">Volver al sitio</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-rose-400 hover:bg-rose-900/20 rounded-md transition-colors"
          >
            <span className="material-icons-round text-[20px]">logout</span>
            <span className="text-sm font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-h-screen flex flex-col">
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 md:px-8 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="md:hidden text-slate-500 hover:text-emerald-700 transition-colors"
              aria-label="Abrir menú"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="material-icons-round">menu</span>
            </button>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">{pageTitle}</h2>
            <div className="hidden sm:block h-4 w-[1px] bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <nav className="hidden sm:flex items-center text-sm text-slate-500">
              <span>Admin</span>
              <span className="material-icons-round text-sm mx-1">chevron_right</span>
              <span className="text-emerald-700 dark:text-emerald-400 font-medium">{breadcrumbCurrent}</span>
            </nav>
          </div>
          <div className="flex items-center gap-4 md:gap-6">
            <button
              type="button"
              className="text-slate-500 hover:text-emerald-700 transition-colors"
              aria-label="Notificaciones"
            >
              <span className="material-icons-round">notifications</span>
            </button>
            <div className="flex items-center gap-3 border-l border-slate-200 dark:border-slate-700 pl-4 md:pl-6">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-slate-800 dark:text-white">{displayName}</p>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-tighter ${roleBadgeClass}`}>
                  {roleLabel}
                </span>
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                {initials || 'A'}
              </div>
            </div>
          </div>
        </header>

        <div className={`admin-content p-6 md:p-8 space-y-8 mx-auto w-full ${isWideContent ? 'max-w-[95%] 2xl:max-w-[92%]' : 'max-w-7xl'}`}>
          {children}
          <div className="flex justify-between items-center text-xs text-slate-400 dark:text-slate-500 pt-4 border-t border-slate-200 dark:border-slate-800">
            <p>© {new Date().getFullYear()} {systemName} Admin System</p>
            <div className="flex gap-4">
              <a className="hover:text-emerald-700 dark:hover:text-emerald-400" href="#">Soporte Técnico</a>
              <a className="hover:text-emerald-700 dark:hover:text-emerald-400" href="#">Manual de Usuario</a>
            </div>
          </div>
        </div>
      </main>

      <button
        type="button"
        className="fixed bottom-6 right-6 w-12 h-12 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-amber-400 hover:scale-110 transition-transform z-50"
        onClick={toggleDarkMode}
        aria-label="Alternar modo oscuro"
      >
        <span className="material-icons-round dark:hidden">dark_mode</span>
        <span className="material-icons-round hidden dark:block">light_mode</span>
      </button>
    </div>
  )
}

export default AdminLayout
